import React, { useRef, useState, useEffect, useMemo, useCallback, useContext } from "react";
import { motion } from "framer-motion";
import { FaHeart, FaShare, FaPlay, FaPause } from "react-icons/fa";
import { useLocation, useParams, Link } from "react-router-dom";
import { Star, Gamepad2, Joystick, Info, Keyboard, Tag, Lightbulb } from "lucide-react";
import { GoScreenFull } from "react-icons/go";
import { useSEO } from "../Pages/Seo";
import AOS from "aos";
import "aos/dist/aos.css";
import { DataContext } from "../Context/DataContext";
import { Helmet } from "react-helmet-async";

const PlayGame = () => {
  const { slug } = useParams();
  const location = useLocation();
  const iframeRef = useRef(null);
  // eslint-disable-next-line no-unused-vars
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isFavorite, setIsFavorite] = useState(false);
  const [isGamePaused, setIsGamePaused] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const rawAllGames = useContext(DataContext);
  const AllGames = useMemo(() => (Array.isArray(rawAllGames) ? rawAllGames : []), [rawAllGames]);

  useEffect(() => {
    AOS.init({
      duration: 500,
      easing: "ease-in-out",
      once: true,
    });
  }, []);

  const createSlug = (text) => {
    return text
      ?.toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const game = useMemo(() => {
    if (location.state?.game) return location.state.game;

    if (AllGames.length > 0 && slug) {
      return AllGames.find(g => createSlug(g.name) === slug);
    }
    return null;
  }, [location.state, AllGames, slug]);

  const getGameUrl = useCallback((originalUrl) => {
  if (!originalUrl) return "";
  return originalUrl.includes("http") ? originalUrl : atob(originalUrl);
}, []);

  const gameUrl = useMemo(() => {
    return getGameUrl(game?.iframeSrc || game?.link);
  }, [game, getGameUrl]);

  const handleFullScreen = useCallback(() => {
    const elem = iframeRef.current;
    if (!elem) return;

    const isCurrentlyFullscreen =
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement ||
      document.msFullscreenElement;

    if (!isCurrentlyFullscreen) {
      if (elem.requestFullscreen) {
        elem.requestFullscreen().catch((err) => console.log("Fullscreen error:", err));
      } else if (elem.webkitRequestFullscreen) {
        elem.webkitRequestFullscreen(); 
      } else if (elem.mozRequestFullScreen) {
        elem.mozRequestFullScreen(); 
      } else if (elem.msRequestFullscreen) {
        elem.msRequestFullscreen(); 
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen(); 
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen(); 
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen(); 
      }
    }
  }, []);

  useEffect(() => {
    const handleChange = () => {
      const isCurrentlyFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      );
      setIsFullscreen(isCurrentlyFullscreen);
    };

    document.addEventListener("fullscreenchange", handleChange);
    document.addEventListener("webkitfullscreenchange", handleChange); 
    document.addEventListener("mozfullscreenchange", handleChange);    
    document.addEventListener("MSFullscreenChange", handleChange);     

    return () => {
      document.removeEventListener("fullscreenchange", handleChange);
      document.removeEventListener("webkitfullscreenchange", handleChange);
      document.removeEventListener("mozfullscreenchange", handleChange);
      document.removeEventListener("MSFullscreenChange", handleChange);
    };
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  const handleIframeLoad = useCallback(() => {
    setTimeout(() => setIsLoading(false), 1000);
  }, []);

  const handleFavorite = useCallback(() => {
    setIsFavorite(prev => !prev);
  }, []);

  const handleShare = useCallback(async () => {
    if (!game) return;
    if (navigator.share) {
      await navigator.share({
        title: game.name,
        text: `Play ${game.name} on Game Zone Hub for free!`,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert("Game link copied!");
    }
  }, [game]);

  const seoData = useMemo(() => ({
    title: game ? `Play ${game.name} Free Online | Game Zone Hub` : "Game Not Found",
    description: game ? game.info || `Play ${game.name} free online directly in your browser. No downloads required.` : "Game not found.",
    keywords: game ? `play ${game.name}, ${game.name} free online, ${game.name} browser game, free html5 games, Game Zone Hub` : "free games",
    canonical: `https://www.gamezonehub.in/play/${slug}`,
  }), [game, slug]);  

  useSEO(seoData);

  const relatedGames = useMemo(() => {
      if (!game || AllGames.length === 0) return [];
      return AllGames.filter(g => g.name !== game.name).slice(0, 4);
  }, [game, AllGames]);

  if (!game) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#141414] via-[#1f1f1f] to-[#141414] flex items-center justify-center p-4">
          <div className="text-center text-white">
              <h2 className="text-3xl font-bold mb-4">Game Not Found</h2>
              <Link to="/" className="px-6 py-3 bg-blue-500 rounded-full hover:bg-blue-600 transition">Back to Home</Link>
          </div>
      </div>
    );
  }

  const schemaData = game ? {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "VideoGame",
        "name": game.name,
        "description": game.desc || `Play ${game.name} online for free at Game Zone Hub.`,
        "url": `https://www.gamezonehub.in/play/${slug}`,
        "image": game.image,
        "genre": game.info?.split("|")[0]?.replace("Genre: ", "") || "Browser Game",
        "playMode": "SinglePlayer",
        "applicationCategory": "Game",
        "operatingSystem": "Web Browser",
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "USD",
          "availability": "https://schema.org/InStock"
        }
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": `Is ${game.name} completely free to play?`,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": `Yes! ${game.name} is 100% free to play. You do not need to download any files or pay for any subscriptions to enjoy the game.`
            }
          },
          {
            "@type": "Question",
            "name": `How do I play ${game.name} on my phone or PC?`,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": `Simply click the 'Play Now' button. ${game.name} uses HTML5 technology, meaning it will load instantly in your Chrome, Safari, or Edge browser without needing any app installations.`
            }
          }
        ]
      }
    ]
  } : null;

  return (
    <motion.div
      className="min-h-screen pt-11 bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] relative overflow-hidden"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <Helmet>
        {schemaData && (
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />
        )}
      </Helmet>

      <div className="container mx-auto px-4">
        <div className="relative z-10 pt-16 sm:pt-20 pb-6 sm:pb-8 px-3 sm:px-4">
            
          {/* Breadcrumb */}
          <nav className="flex text-gray-400 text-sm mb-6 ml-2" aria-label="Breadcrumb">
              <ol className="inline-flex items-center space-x-1 md:space-x-3">
                  <li className="inline-flex items-center">
                      <Link to="/" className="hover:text-white transition-colors">Home</Link>
                  </li>
                  <li>/</li>
                  <li>
                      <Link to="/games" className="hover:text-white transition-colors">Games</Link>
                  </li>
                  <li>/</li>
                  <li className="text-blue-400 font-medium truncate max-w-[150px]">{game.name}</li>
              </ol>
          </nav>

          {/* Game Title & Badges */}
          <div className="max-w-7xl mx-auto mb-6 sm:mb-8" data-aos="fade-down">
            <div className="text-center mb-4 sm:mb-6">
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-cyan-400 to-white mb-3 flex items-center justify-center gap-3">
                <Gamepad2 className="w-8 h-8 sm:w-10 sm:h-10 text-blue-400" />
                  Play {game.name}
              </h1>
              <div className="flex flex-wrap items-center justify-center gap-3 text-white/80 text-xs sm:text-sm md:text-base mt-4">
                <div className="flex items-center gap-2 bg-white/5 px-4 py-1.5 rounded-full border border-white/10">
                  <Tag className="text-cyan-400" size={14} /> <span>100% Free to Play</span>
                </div>
                <div className="flex items-center gap-2 bg-white/5 px-4 py-1.5 rounded-full border border-white/10">
                  <Joystick className="text-purple-400" size={14} /> <span>HTML5 Instant Play</span>
                </div>
                <div className="flex items-center gap-2 bg-white/5 px-4 py-1.5 rounded-full border border-white/10">
                  <Star className="text-yellow-400" size={14} /> <span>No Download Needed</span>
                </div>
              </div>
            </div>

            {/* Action Bar */}
            <div className="bg-black/30 backdrop-blur-lg rounded-2xl p-3 sm:p-4 mb-6 border border-white/10 flex flex-wrap items-center justify-between gap-4">
                <button
                  onClick={() => setIsGamePaused(!isGamePaused)}
                  className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white transition-all text-sm font-medium"
                >
                  {isGamePaused ? <FaPlay size={14} /> : <FaPause size={14} />}
                  {isGamePaused ? 'Resume' : 'Pause'}
                </button>

                <div className="flex items-center gap-3">
                  <button onClick={handleFavorite} className={`p-3 rounded-full transition-all ${isFavorite ? 'bg-blue-500 text-white' : 'bg-white/10 text-white hover:bg-white/20'}`}>
                    <FaHeart size={16} />
                  </button>
                  <button onClick={handleShare} className="p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-all">
                    <FaShare size={16} />
                  </button>
                  <button onClick={handleFullScreen} className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-full font-semibold hover:scale-105 transition-transform text-sm">
                    <GoScreenFull size={16} /> Full Screen
                  </button>
                </div>
            </div>
          </div>

          {/* Game Iframe Wrapper */}
          <div className="max-w-7xl mx-auto" data-aos="fade-up">
            <div className="relative bg-[#0f172a] rounded-2xl sm:rounded-3xl p-1 border border-white/20 shadow-2xl">
              {isLoading && (
                <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/80 rounded-3xl">
                  <div className="w-16 h-16 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin"></div>
                </div>
              )}

              <div className="relative rounded-xl sm:rounded-[20px] overflow-hidden bg-black">
                <iframe
                  id="game"
                  ref={iframeRef}
                  src={gameUrl}
                  title={`${game.name} Game`}
                  className="w-full h-[400px] sm:h-[500px] lg:h-[650px] border-0 rounded-xl"
                  allow="autoplay; fullscreen; microphone; clipboard-read; clipboard-write"
                  allowFullScreen
                  scrolling="no"
                  onLoad={handleIframeLoad}
                  style={{ opacity: isLoading ? 0 : 1, transition: "opacity 0.5s" }}
                />

                {!gameStarted && (
                  <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/80 backdrop-blur-sm">
                    <button
                      onClick={() => {
                        handleFullScreen();
                        setGameStarted(true);
                      }}
                      className="px-8 py-4 bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-bold rounded-full hover:scale-105 transition-transform text-xl flex items-center gap-3 shadow-[0_0_20px_rgba(59,130,246,0.5)]"
                    >
                      <FaPlay /> Play Now
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* 🚀 HIGH QUALITY UNIQUE CONTENT SECTION FOR ADSENSE */}
          <div className="grid lg:grid-cols-3 gap-8 mt-12 max-w-7xl mx-auto">
              
              <div className="lg:col-span-2 space-y-8">
                  <article className="bg-[#1e293b] shadow-xl rounded-3xl p-6 sm:p-10 border border-white/5 text-gray-300">
                      
                      {/* 1. About Section */}
                      <section className="mb-10">
                        <h2 className="text-2xl sm:text-3xl font-bold text-white mb-5 flex flex-wrap items-center gap-3">
                            <Info className="text-cyan-400" /> About {game.name}
                        </h2>
                        
                        <div className="prose prose-invert max-w-none space-y-4 text-base leading-relaxed">
                            <p>{game.desc}</p>
                        </div>
                        
                        <div className="mt-6 flex flex-wrap gap-3">
                            <span className="px-4 py-2 bg-black/30 border border-white/10 rounded-full text-sm text-gray-300 flex items-center gap-2"><Tag size={14}/> Free to Play</span>
                            <span className="px-4 py-2 bg-black/30 border border-white/10 rounded-full text-sm text-gray-300 flex items-center gap-2"><Tag size={14}/> HTML5 Supported</span>
                            <span className="px-4 py-2 bg-black/30 border border-white/10 rounded-full text-sm text-gray-300 flex items-center gap-2"><Tag size={14}/> No Downloads</span>
                            <span className="px-4 py-2 bg-black/30 border border-white/10 rounded-full text-sm text-gray-300 flex items-center gap-2"><Tag size={14}/> Cross-Platform</span>
                        </div>
                      </section>

                      {/* 2. Controls & Instructions */}
                      {game.instruction && (
                        <section className="mb-10">
                          <h2 className="text-2xl font-bold text-white mb-5 flex flex-wrap items-center gap-3">
                               <Keyboard className="text-yellow-400" /> Controls & How to Play
                          </h2>
                          <div className="bg-black/30 rounded-2xl p-6 border border-white/5">
                            <p className="mb-0 whitespace-pre-line leading-relaxed text-gray-300">{game.instruction}</p>
                          </div>
                        </section>
                      )}

                      {/* 3. Gameplay Tips & Strategy */}
                      <section className="mb-10">
                        <h2 className="text-2xl font-bold text-white mb-5 flex flex-wrap items-center gap-3">
                            <Lightbulb className="text-amber-400" /> Tips & Strategies for Success
                        </h2>
                        <div className="bg-gradient-to-br from-black/40 to-blue-950/20 rounded-2xl p-6 border border-white/5 space-y-3">
                          <div className="flex items-start gap-3">
                            <div className="w-2 h-2 rounded-full bg-cyan-400 mt-2 shrink-0"></div>
                            <p className="text-sm sm:text-base text-gray-300"><strong>Patience Beats Speed:</strong> Take a moment to analyze the layout before making rapid moves. Careful planning prevents unexpected mistakes.</p>
                          </div>
                          <div className="flex items-start gap-3">
                            <div className="w-2 h-2 rounded-full bg-cyan-400 mt-2 shrink-0"></div>
                            <p className="text-sm sm:text-base text-gray-300"><strong>Master the Controls:</strong> Spend the first few rounds getting accustomed to sensitivity on your device (keyboard arrow keys vs mobile touchscreen taps).</p>
                          </div>
                          <div className="flex items-start gap-3">
                            <div className="w-2 h-2 rounded-full bg-cyan-400 mt-2 shrink-0"></div>
                            <p className="text-sm sm:text-base text-gray-300"><strong>Use Fullscreen Mode:</strong> Click the "Full Screen" button above for an immersive view that prevents accidental clicks outside the canvas.</p>
                          </div>
                        </div>
                      </section>

                      {/* 4. Compatibility & Specifications */}
                      <section className="mb-10">
                        <h2 className="text-2xl font-bold text-white mb-5 flex flex-wrap items-center gap-3">
                            <Joystick className="text-purple-400" /> Device & Technical Specifications
                        </h2>
                        <div className="grid sm:grid-cols-2 gap-4 text-sm">
                          <div className="p-4 bg-black/30 rounded-xl border border-white/5">
                            <span className="text-gray-400 block text-xs uppercase font-semibold mb-1">Supported Platforms</span>
                            <span className="text-white font-medium">Desktop, Mobile (iOS & Android), Tablet, Chromebook</span>
                          </div>
                          <div className="p-4 bg-black/30 rounded-xl border border-white/5">
                            <span className="text-gray-400 block text-xs uppercase font-semibold mb-1">Browser Engine</span>
                            <span className="text-white font-medium">HTML5 / WebGL (Chrome, Safari, Firefox, Edge)</span>
                          </div>
                          <div className="p-4 bg-black/30 rounded-xl border border-white/5">
                            <span className="text-gray-400 block text-xs uppercase font-semibold mb-1">Input Methods</span>
                            <span className="text-white font-medium">Keyboard, Mouse, Touch Screen</span>
                          </div>
                          <div className="p-4 bg-black/30 rounded-xl border border-white/5">
                            <span className="text-gray-400 block text-xs uppercase font-semibold mb-1">Installation</span>
                            <span className="text-white font-medium">No Download Required (Instant Play)</span>
                          </div>
                        </div>
                      </section>

                      {/* 5. Troubleshooting */}
                      <section className="mb-10">
                        <h2 className="text-2xl font-bold text-white mb-5 border-b border-white/10 pb-3">
                            Troubleshooting & Frequently Asked Questions
                        </h2>
                        <div className="space-y-6">
                          <div>
                            <h3 className="font-bold text-lg text-cyan-400 mb-2">Is {game.name} completely free to play?</h3>
                            <p className="text-gray-300">Yes! {game.name} is 100% free to play at Game Zone Hub. You do not need to register an account, enter payment details, or pay for any in-game subscriptions.</p>
                          </div>
                          <div>
                            <h3 className="font-bold text-lg text-cyan-400 mb-2">Do I need to download or install anything to play?</h3>
                            <p className="text-gray-300">No installation or browser plugins are needed. Modern HTML5 technology lets the game launch immediately within your browser tab.</p>
                          </div>
                          <div>
                            <h3 className="font-bold text-lg text-cyan-400 mb-2">What should I do if the game freezes or doesn't load?</h3>
                            <p className="text-gray-300">If the game canvas appears black or fails to respond, refresh your browser window (`Ctrl + R` or `Cmd + R`), disable intrusive ad-blockers, and ensure your browser's hardware acceleration is enabled in settings.</p>
                          </div>
                          <div>
                            <h3 className="font-bold text-lg text-cyan-400 mb-2">Can I play {game.name} on my mobile phone?</h3>
                            <p className="text-gray-300">Yes, {game.name} automatically scales to touchscreens on mobile devices and tablets.</p>
                          </div>
                        </div>
                      </section>

                  </article>
              </div>

              {/* Sidebar: More Like This */}
              <div className="lg:col-span-1">
                  <div className="bg-[#1e293b] rounded-3xl p-6 border border-white/5 sticky top-24 shadow-xl">
                      <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                          <Gamepad2 className="text-blue-400"/> Similar Games
                      </h3>
                      <div className="space-y-4">
                          {relatedGames.map((relatedGame, idx) => (
                              <Link 
                                key={idx} 
                                to={`/play/${createSlug(relatedGame.name)}`} 
                                state={{ game: relatedGame }}
                                className="flex gap-4 items-center group p-3 hover:bg-white/5 rounded-xl transition-all border border-transparent hover:border-white/10"
                              >
                                  <img 
                                    src={relatedGame.image} 
                                    alt={relatedGame.name}
                                    loading="lazy" 
                                    className="w-20 h-20 rounded-lg object-cover group-hover:scale-105 transition-transform shadow-md"
                                  />
                                  <div>
                                      <h4 className="text-white font-bold text-sm group-hover:text-blue-400 transition-colors line-clamp-2">
                                          {relatedGame.name}
                                      </h4>
                                      <span className="text-xs text-blue-400 mt-2 inline-block font-semibold bg-blue-500/10 px-2 py-1 rounded">Play Now</span>
                                  </div>
                              </Link>
                          ))}
                      </div>
                  </div>
              </div>

          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default PlayGame;